import Api from 'api';
import { isNativeApp, openExternalUrl, validateTrustedHttpsUrl } from './nativeApp';

/**
 * T2-7437: open the talent's Stripe Express page without leaving the current
 * screen. POST /v3/hygienists/me/stripeLink returns the set-up link for temps
 * who have not finished Stripe Express, and their Express dashboard link for
 * everyone else; both are on connect.stripe.com.
 *
 * - Native app: the system browser (Safari / Chrome), like Profile's
 *   "View Express Payouts". The iOS WebView ignores window.open, which is why
 *   the invoice "OK" used to do nothing.
 * - Web: a tab opened during the tap, so the browser allows it, then pointed at
 *   Stripe once the link arrives. If the browser still blocks it, the caller
 *   gets the link to show as a button.
 *
 * `canOpen` is asked again once the link arrives. When it answers false (the
 * screen that asked is no longer current, for example the talent left the
 * invoice or the account changed), nothing opens and the waiting tab closes.
 *
 * Never throws.
 */
export const STRIPE_EXPRESS_HOSTS = Object.freeze(['connect.stripe.com']);
export const STRIPE_EXPRESS_OPEN_ERROR = "We couldn't open your Stripe Express account. Please try again.";
export const STRIPE_EXPRESS_TAB_BLOCKED = 'Your browser blocked the new tab. Use the link below to open Stripe Express.';

export type StripeExpressOpenFailure = { opened: false; message: string; url?: string };
export type StripeExpressOpenCancelled = { opened: false; cancelled: true };
export type StripeExpressOpenResult = { opened: true } | StripeExpressOpenFailure | StripeExpressOpenCancelled;

const canOpenSystemBrowser = (): boolean => isNativeApp()
  && typeof (window as any).cordova?.InAppBrowser?.open === 'function';

const closeTab = (tab: Window | null) => {
  try {
    tab?.close();
  } catch {
    // already closed
  }
};

export async function openStripeExpress(canOpen: () => boolean = () => true): Promise<StripeExpressOpenResult> {
  const native = canOpenSystemBrowser();
  let pendingTab: Window | null = null;
  if (!native) {
    try {
      pendingTab = window.open('', '_blank');
    } catch {
      pendingTab = null;
    }
  }
  try {
    const link = await Api.getStripeAccountLoginLink();
    if (!canOpen()) {
      closeTab(pendingTab);
      return { opened: false, cancelled: true };
    }
    const destination = validateTrustedHttpsUrl(String(link?.url || ''), STRIPE_EXPRESS_HOSTS);
    if (native) {
      openExternalUrl(destination, STRIPE_EXPRESS_HOSTS);
      return { opened: true };
    }
    if (pendingTab && !pendingTab.closed) {
      try {
        pendingTab.opener = null;
      } catch {
        // some browsers do not allow clearing it; the tab still opens
      }
      pendingTab.location.href = destination;
      return { opened: true };
    }
    return { opened: false, message: STRIPE_EXPRESS_TAB_BLOCKED, url: destination };
  } catch (error) {
    closeTab(pendingTab);
    console.error('[stripeExpressLink] could not open Stripe Express', error);
    return canOpen() ? { opened: false, message: STRIPE_EXPRESS_OPEN_ERROR } : { opened: false, cancelled: true };
  }
}
